import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  AcademicCapIcon,
  CodeBracketIcon,
  ChartBarIcon,
  CloudIcon,
  CpuChipIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import emailjs from 'emailjs-com';
import SEO from '../components/SEO';
import { organizationStructuredData } from '../data/seo';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Initialize EmailJS with your public key
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your_service_id';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'your_template_id';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key';
      
      // Send email using EmailJS
      const result = await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
          to_name: 'Duke',
          reply_to: formData.email,
        },
        publicKey
      );
      
      console.log('Email sent successfully:', result);
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
    } catch (error) {
      console.error('Error sending email:', error);
      setIsSubmitting(false);
      setSubmitStatus('error');
    }
  };

  const contactInfo = [
    {
      icon: EnvelopeIcon,
      title: 'Email',
      value: 'teachwithduke@gmail.com',
      link: 'mailto:teachwithduke@gmail.com'
    },
    {
      icon: PhoneIcon,
      title: 'Phone',
      value: '+254745711132',
      link: 'tel:+254745711132'
    },
    {
      icon: MapPinIcon,
      title: 'Location',
      value: 'Global - Online Sessions',
      link: null
    },
    {
      icon: GlobeAltIcon,
      title: 'Availability',
      value: 'Worldwide',
      link: null
    }
  ];

  const socialLinks = [
    { name: 'LinkedIn', href: 'https://linkedin.com/in/duke-nyamongo-74a043178/', icon: '💼' },
    { name: 'GitHub', href: 'https://github.com/dukelester', icon: '🐙' },
    { name: 'Twitter', href: 'https://twitter.com/teachwithduke', icon: '🐦' },
    { name: 'YouTube', href: 'https://youtube.com/@teachwithduke', icon: '📺' },
  ];

  const expertise = [
    { icon: CodeBracketIcon, name: 'Python Programming', color: 'text-green-500' },
    { icon: GlobeAltIcon, name: 'Web Development', color: 'text-blue-500' },
    { icon: ChartBarIcon, name: 'Data Analysis', color: 'text-purple-500' },
    { icon: CpuChipIcon, name: 'Machine Learning', color: 'text-orange-500' },
    { icon: CloudIcon, name: 'DevOps & Cloud', color: 'text-indigo-500' },
    { icon: AcademicCapIcon, name: 'Career Guidance', color: 'text-pink-500' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title="Contact Duke - Get Help with Programming & Tech Career Guidance"
        description="Contact Duke for personalized programming mentorship, course inquiries, or technical support. Get in touch via email, phone, or contact form. Quick response times and global availability."
        keywords="contact programming mentor, get help with coding, programming tutor contact, tech career guidance, coding support, programming consultation"
        canonical="/contact"
        ogImage="/og-contact.jpg"
        structuredData={organizationStructuredData}
      />
      {/* Header */}
      <section className="bg-gradient-to-r from-indigo-700 to-cyan-600 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">
              Get In Touch
            </h1>
            <p className="text-xl md:text-2xl text-cyan-100 max-w-3xl mx-auto">
              Ready to start your learning journey? Have questions about courses or need help with a project? 
              I'm here to help you succeed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 dark:bg-indigo-900 rounded-full mb-4">
                  <info.icon className="h-8 w-8 text-indigo-600 dark:text-cyan-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {info.title}
                </h3>
                {info.link ? (
                  <a
                    href={info.link}
                    className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-cyan-300 transition-colors duration-200"
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-gray-600 dark:text-gray-400">
                    {info.value}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Send a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-indigo-600 to-cyan-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-700 hover:to-cyan-600 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center text-green-600 dark:text-green-400"
                  >
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Message sent successfully! I'll get back to you soon.
                  </motion.div>
                )}
                
                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center text-red-600 dark:text-red-400"
                  >
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                    Failed to send message. Please try again or contact me directly at teachwithduke@gmail.com
                  </motion.div>
                )}
              </form>
            </motion.div>

            {/* Contact Info and Expertise */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Response Time */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Response Time
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-gray-700 dark:text-gray-300">
                      <strong>Email:</strong> Within 24 hours
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                    <span className="text-gray-700 dark:text-gray-300">
                      <strong>Urgent queries:</strong> Same day
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-gray-700 dark:text-gray-300">
                      <strong>Course inquiries:</strong> Within 12 hours
                    </span>
                  </div>
                </div>
              </div>

              {/* Areas of Expertise */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Areas of Expertise
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {expertise.map((area, index) => (
                    <div key={index} className="flex items-center">
                      <area.icon className={`h-5 w-5 mr-2 ${area.color}`} />
                      <span className="text-gray-700 dark:text-gray-300 text-sm">
                        {area.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Social Links */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Connect With Me
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
                    >
                      <span className="text-xl mr-3">{social.icon}</span>
                      <span className="text-gray-700 dark:text-gray-300 font-medium">
                        {social.name}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Common questions about courses and sessions
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: "How quickly can I expect a response?",
                answer: "I typically respond to emails within 24 hours. For urgent course inquiries, I aim to respond within 12 hours."
              },
              {
                question: "Do you offer group sessions?",
                answer: "Currently, I focus on 1-on-1 sessions to provide personalized attention. However, I can accommodate small groups (2-3 people) for specific topics."
              },
              {
                question: "What time zones do you work with?",
                answer: "I work with students worldwide and can accommodate various time zones. I'm flexible with scheduling to find times that work for both of us."
              },
              {
                question: "Can I get help with my existing projects?",
                answer: "Absolutely! I offer project support sessions where we can work through your code, debug issues, and improve your implementation."
              },
              {
                question: "Do you provide certificates or credentials?",
                answer: "While I don't provide formal certificates, I can provide letters of recommendation and help you build a portfolio that demonstrates your skills."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 dark:bg-slate-700 rounded-2xl p-6 border border-slate-200 dark:border-slate-600"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
